'use client';
import React, { useEffect, useRef, useState } from 'react';
import {
  useField,
  FieldInputProps,
  FieldMetaProps,
  FieldHelperProps,
} from 'formik';
import { t } from 'i18next';

export interface FileInfo {
  name: string;
  size: string;
  icon: string;
  index: number;
  file: File | FileType;
  isUploading: boolean;
  progress?: number;
  id?: number;
}

interface FileUploadProps {
  allowedFileTypes: string[] | string;
  initialFiles?: (File | FileType)[];
  name: string;
  progress?: number;
  rows?: boolean;
  onCancelUpload?: () => void;
  labelClass?: string;
  containerClass?: string;
  customizeUpload?: boolean;
  labelText?: string;
  uploadImage?: string;
  imageClass?: string;
  contentClassName?: string;
  filesContainerClassName?: string;
  targetContainerId?: string;
  inputFileClassName?: string;
  textLabelClass?: string;
  useCustomCard?: boolean;
  customCardRenderer?: (
    fileInfo: FileInfo,
    onRemove: () => void,
  ) => React.ReactNode;
  defaultCardRenderer?: (
    fileName: string,
    fileSize: string,
    iconType: string,
    onRemove: () => void,
    isUploading?: boolean,
    progress?: number,
  ) => React.ReactNode;
  parentClassName?: string;
  inputContainerClassName?: string;
  fileCardsContainerClassName?: string;
  existingFilesContainerClassName?: string;
  errorsInside?: boolean;
  newFilesContainerClassName?: string;
  onCustomDelete?: (id: number) => void;
  existingFiles?: (File | FileType)[];
  labelDesc?: string;
  isUploadVisible?: boolean;
}

const MAX_FILE_SIZE_MB = 10;
const MAX_FILE_SIZE_BYTES = MAX_FILE_SIZE_MB * 1024 * 1024;

const FileUpload: React.FC<FileUploadProps> = ({
  allowedFileTypes,
  initialFiles = [],
  progress = 0,
  labelClass,
  rows = true,
  onCancelUpload,
  customizeUpload = false,
  containerClass = '',
  imageClass = '',
  labelText = '',
  uploadImage = '/icons/upload.svg',
  filesContainerClassName = 'minthr-sidebar-content-files new mt-3',
  targetContainerId = '',
  inputFileClassName = '',
  textLabelClass = 'minthr-color-primary',
  contentClassName = 'minthr-flex minthr-flex-items-center minthr-flex-justify-center',
  useCustomCard = false,
  errorsInside = false,
  customCardRenderer,
  defaultCardRenderer,
  parentClassName = '',
  inputContainerClassName = '',
  fileCardsContainerClassName = '',
  existingFilesContainerClassName = '',
  newFilesContainerClassName = '',
  existingFiles = [],
  onCustomDelete,
  labelDesc,
  isUploadVisible = true,
  ...props
}) => {
  const [field, meta, helpers]: [
    FieldInputProps<(File | FileType)[]>,
    FieldMetaProps<(File | FileType)[]>,
    FieldHelperProps<(File | FileType)[]>,
  ] = useField<(File | FileType)[]>({ ...props, type: 'file' });

  const [progressFile, setProgressFile] = useState<{ [key: number]: number }>(
    {},
  );
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [fileSizeError, setFileSizeError] = useState<string | null>(null);

  const isDuplicateFile = (
    file: File,
    existingFiles: (File | FileType)[],
  ): boolean => {
    return existingFiles.some((existingFile): boolean => {
      if (isFileObject(existingFile)) {
        return (
          existingFile.name === file.name && existingFile.size === file.size
        );
      }
      return existingFile.name === file.name;
    });
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    const selectedFiles = Array.from(e.target.files || []);
    const newFiles: File[] = [];
    const oversizedFiles: string[] = [];
    const currentFiles = field.value || [];

    setFileSizeError(null);

    for (const file of selectedFiles) {
      if (file.size > MAX_FILE_SIZE_BYTES) {
        oversizedFiles.push(
          `${file.name} (${(file.size / (1024 * 1024)).toFixed(2)} MB)`,
        );
        continue;
      }

      if (!isFileAllowed(file.name)) {
        continue;
      }

      if (isDuplicateFile(file, [...currentFiles, ...existingFiles])) {
        continue;
      }

      newFiles.push(file);
    }

    if (oversizedFiles.length > 0) {
      setFileSizeError(
        `${t('fileSizeExceeded', { maxSize: MAX_FILE_SIZE_MB })} : ${oversizedFiles.join(', ')}`,
      );
    }

    if (newFiles.length === 0) {
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      return;
    }

    const combinedFiles = [...currentFiles, ...newFiles];
    helpers.setValue(combinedFiles);

    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }

    newFiles.forEach((_, index) => {
      const fileIndex = (field.value || []).length + index;
      simulateUpload(fileIndex);
    });
  };

  const isFileAllowed = (fileName: string): boolean => {
    if (allowedFileTypes === '*') return true;

    const ext = fileName.split('.').pop()?.toLowerCase();
    return (allowedFileTypes as string[])
      .map((x) => x.toLowerCase())
      .includes(ext || '');
  };

  const handleFileRemove = (
    index: number,
    fileSource: 'existing' | 'formik',
  ) => {
    if (fileSource === 'existing') {
      const fileToRemove = existingFiles[index];
      if (fileToRemove && onCustomDelete) {
        if (
          !isFileObject(fileToRemove) &&
          'id' in fileToRemove &&
          fileToRemove.id
        ) {
          onCustomDelete(fileToRemove.id);
        }
      }
    } else {
      // Handle removal of Formik managed files
      const updatedFiles = (field.value || []).filter((_, i) => i !== index);
      helpers.setValue(updatedFiles);

      // Update progress tracking - shift indexes
      const updatedProgress = { ...progressFile };
      delete updatedProgress[index];
      Object.keys(updatedProgress).forEach((key) => {
        const numKey = parseInt(key);
        if (numKey > index) {
          updatedProgress[numKey - 1] = updatedProgress[numKey];
          delete updatedProgress[numKey];
        }
      });
      setProgressFile(updatedProgress);

      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const isFileObject = (file: File | FileType): file is File => {
    return 'size' in file;
  };

  const getFileName = (file: File | FileType): string => {
    return isFileObject(file) ? file.name : file.name || 'Unknown file';
  };

  const getFileSizeDisplay = (file: File | FileType): string => {
    if (isFileObject(file)) {
      return (file.size / (1024 * 1024)).toFixed(2) + ' MB';
    }
    return '2 MB';
  };

  const getFileIconType = (fileName: string): string => {
    const extension = fileName.split('.').pop()?.toLowerCase() || '';

    const iconMap: { [key: string]: string } = {
      pdf: 'doc-pdf',
      doc: 'doc-word',
      docx: 'doc-word',
      txt: 'doc-text',
      rtf: 'doc-text',

      xls: 'doc-excel',
      xlsx: 'doc-excel',
      csv: 'doc-excel',

      ppt: 'doc-pp',
      pptx: 'doc-pp',

      jpg: 'doc-img',
      jpeg: 'doc-img',
      png: 'doc-img',
      gif: 'doc-img',
      bmp: 'doc-img',
      svg: 'doc-img',
    };

    return iconMap[extension] || 'document';
  };

  const getFileIcon = (fileName?: string): string => {
    if (fileName) {
      const iconType = getFileIconType(fileName);
      return `/icons/${iconType}.svg`;
    }

    if (rows) {
      return '/icons/doc-file.svg';
    }
    return '/icons/accounting.svg';
  };

  const createFileInfo = (file: File | FileType, index: number): FileInfo => {
    const fileName = getFileName(file);
    const isUploading =
      progressFile[index] !== undefined &&
      progressFile[index] < 100 &&
      isFileObject(file);

    return {
      name: getFileName(file),
      size: getFileSizeDisplay(file),
      icon: getFileIcon(fileName),
      index,
      file,
      isUploading,
      progress: progressFile[index],
      id: isFileObject(file as FileType) ? (file as FileType).id : undefined,
    };
  };

  const renderDefaultCard = (fileInfo: FileInfo, onRemove: () => void) => {
    if (defaultCardRenderer) {
      return defaultCardRenderer(
        fileInfo.name,
        fileInfo.size,
        getFileIconType(fileInfo.name),
        onRemove,
        fileInfo.isUploading,
        fileInfo.progress,
      );
    }

    if (rows) {
      return (
        <div
          key={`file-${fileInfo.index}`}
          className="minthr-flex minthr-flex-items-center minthr-bg-light-gray minthr-text-decoration-none minthr-p-2 minthr-border-rounded minthr-mb-3"
        >
          <img
            className="minthr-icon-xl minthr-mr-2"
            src={fileInfo.icon}
            alt="Document Icon"
          />
          <div className="minthr-mr-6 minthr-pr-3 minthr-text-truncate">
            <h6 title={fileInfo.name}>{fileInfo.name}</h6>
            <div className="text-s">{fileInfo.size}</div>
          </div>
          {!fileInfo.isUploading ? (
            <button
              onClick={onRemove}
              type="button"
              className="minthr-p-0 minthr-border-none minthr-bg-transparent file-cancel minthr-ml-auto minthr-mr-2"
            >
              <img src="/icons/delete.svg" alt="Delete Icon" />
            </button>
          ) : (
            <div className="minthr-ml-auto minthr-progress-spinner-container">
              <img
                className="minthr-loading-spinner"
                src="/icons/spinner-outer.svg"
                alt="Loading Spinner"
              />
              <div className="text-xs minthr-pogress-txt minthr-position-absolute minthr-position-center">
                {fileInfo.progress ? `${fileInfo.progress}%` : '0%'}
              </div>
            </div>
          )}
        </div>
      );
    } else {
      return (
        <div
          className="col-sm-4 minthr-px-1 minthr-mb-2"
          id={`file-${fileInfo.index}`}
          key={`file-${fileInfo.index}`}
        >
          <div className="minthr-box minthr-box-shadow minthr-box-round minthr-p-3 minthr-pb-2 minthr-h-vh-25">
            <div className="minthr-flex minthr-mb-2">
              <div className="minthr-icon-bg minthr-icon-bg-l minthr-bg-secondary minthr-icon-rounded">
                <img
                  className="minthr-icon-m minthr-mobile-icon-s"
                  src={fileInfo.icon}
                  alt=""
                />
              </div>
            </div>
            <div className="h5 minthr-border-b minthr-pb-1 minthr-min-h-70 minthr-break-word minthr-mb-3 minthr-text-3-lines">
              {fileInfo.name}
            </div>
            <div className="minthr-flex inthr-flex-items-center minthr-flex-space-between ">
              <a className="file-cancel" onClick={onRemove}>
                <img
                  className="minthr-icon-s minthr-icon-danger"
                  src="/icons/delete.svg"
                  alt=""
                />
              </a>
            </div>
          </div>
        </div>
      );
    }
  };

  const renderFileCard = (
    file: File | FileType,
    index: number,
    fileSource: 'existing' | 'formik' = 'formik',
  ) => {
    const fileInfo = createFileInfo(file, index);
    const onRemove = () => handleFileRemove(index, fileSource);

    if (useCustomCard && customCardRenderer) {
      return customCardRenderer(fileInfo, onRemove);
    }

    return renderDefaultCard(fileInfo, onRemove);
  };

  // Initialize Formik with initialFiles only once
  useEffect(() => {
    if (
      initialFiles &&
      initialFiles.length > 0 &&
      (!field.value || field.value.length === 0)
    ) {
      helpers.setValue(initialFiles);
    }
  }, [initialFiles, field.value, helpers]);

  useEffect(() => {
    if (targetContainerId) {
      const targetContainer = document.getElementById(targetContainerId);
      const filesContainer = document.getElementById('uploadedFiles');
      if (targetContainer && filesContainer) {
        targetContainer.innerHTML = '';
        targetContainer.appendChild(filesContainer);
      }
    }
  }, [field.value, targetContainerId, existingFiles]);

  const simulateUpload = (index: number) => {
    let percent = 0;
    const interval = setInterval(() => {
      percent += 10;
      setProgressFile((prev) => ({ ...prev, [index]: percent }));

      if (percent >= 100) {
        clearInterval(interval);
      }
    }, 50);
  };

  return (
    <>
      {rows ? (
        <div className={parentClassName}>
          {/* Input Container with custom class */}
          <div className={inputContainerClassName}>
            {customizeUpload && isUploadVisible && (
              <div className={containerClass}>
                <label className={labelClass}>
                  <input
                    type="file"
                    className={`input-file-drop ${inputFileClassName}`}
                    accept={
                      allowedFileTypes == '*' &&
                      !Array.isArray(allowedFileTypes)
                        ? '*'
                        : (allowedFileTypes as string[])
                            .map((ext) => `.${ext}`)
                            .join(',')
                    }
                    onChange={handleFileChange}
                    multiple
                    ref={fileInputRef}
                  />

                  <div className={`${contentClassName}`}>
                    <img
                      className={imageClass}
                      src={uploadImage}
                      alt="Upload Icon"
                    />
                    {labelText && (
                      <div className={`${textLabelClass}`}>{labelText}</div>
                    )}
                  </div>
                </label>
              </div>
            )}
            {!customizeUpload && isUploadVisible && (
              <div className="col-sm-12 minthr-mt-2">
                <label
                  className={`minthr-files minthr-border-rounded minthr-bg-primary-opacity minthr-p-6 upload w-100 ${labelClass}`}
                >
                  <input
                    type="file"
                    className="input-file-drop"
                    accept={
                      allowedFileTypes == '*' &&
                      !Array.isArray(allowedFileTypes)
                        ? '*'
                        : (allowedFileTypes as string[])
                            .map((ext) => `.${ext}`)
                            .join(',')
                    }
                    onChange={handleFileChange}
                    multiple
                    ref={fileInputRef}
                  />

                  <div>
                    <img
                      className="minthr-icon-xl minthr-mb-3"
                      src="/icons/upload.svg"
                      alt="Upload Icon"
                    />
                    <div className="minthr-color-primary">
                      {labelDesc ??
                        t('DragAndDrop.first') + ' ' + t('DragAndDrop.lastCv')}
                    </div>
                  </div>
                </label>
              </div>
            )}
            {errorsInside && meta.touched && meta.error && (
              <div className="text-s minthr-color-danger minthr-mt-2">
                {meta.error}
              </div>
            )}
          </div>

          {!errorsInside && meta.touched && meta.error && (
            <div className="text-s minthr-color-danger">{meta.error}</div>
          )}
          {fileSizeError && (
            <div className="text-s minthr-color-danger minthr-mt-2">
              {fileSizeError}
            </div>
          )}

          {/* Files Container with custom class */}
          <div
            id="uploadedFiles"
            className={`${filesContainerClassName} ${fileCardsContainerClassName}`}
          >
            {/* API Existing Files (rendered first) */}
            {existingFiles.length > 0 && (
              <div className={existingFilesContainerClassName}>
                {existingFiles.map((file, index) => {
                  const fileInfo = createFileInfo(file, index);
                  const onRemove = () => handleFileRemove(index, 'existing');

                  if (useCustomCard && customCardRenderer) {
                    return customCardRenderer(fileInfo, onRemove);
                  }
                  return renderDefaultCard(fileInfo, onRemove);
                })}
              </div>
            )}

            {/* Formik Files (initial + newly uploaded) */}
            {field.value && field.value.length > 0 && (
              <div className={newFilesContainerClassName}>
                {field.value.map((file, index) =>
                  renderFileCard(file, index, 'formik'),
                )}
              </div>
            )}
          </div>
        </div>
      ) : (
        <>
          {!useCustomCard && (
            <div
              className={`col-sm-4 minthr-px-1 minthr-mb-2 demande-action-file-upload minthr-h-vh-25 ${inputContainerClassName}`}
            >
              <label className="minthr-files minthr-border-rounded minthr-bg-primary-opacity h-100 minthr-py-6 minthr-px-3 minthr-hover-light-gray1">
                <input
                  type="file"
                  className="input-file-drop"
                  accept={
                    allowedFileTypes == '*' && !Array.isArray(allowedFileTypes)
                      ? '*'
                      : (allowedFileTypes as string[])
                          .map((ext) => `.${ext}`)
                          .join(',')
                  }
                  onChange={handleFileChange}
                  multiple
                  ref={fileInputRef}
                />
                <div>
                  <img
                    className="minthr-icon-xl minthr-my-3"
                    src="/icons/upload.svg"
                    alt=""
                  />
                  <div className="minthr-color-primary">
                    Ajouter un document
                  </div>
                </div>
              </label>
            </div>
          )}

          {fileSizeError && (
            <div className="text-s minthr-color-danger minthr-mt-2 minthr-px-1">
              {fileSizeError}
            </div>
          )}

          {useCustomCard && (
            <div className={inputContainerClassName}>
              <label className="minthr-files minthr-border-rounded minthr-bg-primary-opacity h-100 minthr-py-6 minthr-px-3 minthr-hover-light-gray1">
                <input
                  type="file"
                  className="input-file-drop"
                  accept={
                    allowedFileTypes == '*' && !Array.isArray(allowedFileTypes)
                      ? '*'
                      : (allowedFileTypes as string[])
                          .map((ext) => `.${ext}`)
                          .join(',')
                  }
                  onChange={handleFileChange}
                  multiple
                  ref={fileInputRef}
                />
                <div>
                  <img
                    className="minthr-icon-xl minthr-my-3"
                    src="/icons/upload.svg"
                    alt=""
                  />
                  <div className="minthr-color-primary">
                    Ajouter un document
                  </div>
                </div>
              </label>
            </div>
          )}

          {/* API Existing Files */}
          {existingFiles.map((file, index) => {
            const fileInfo = createFileInfo(file, index);
            const onRemove = () => handleFileRemove(index, 'existing');

            if (useCustomCard && customCardRenderer) {
              return customCardRenderer(fileInfo, onRemove);
            }
            return renderDefaultCard(fileInfo, onRemove);
          })}

          {/* Formik Files */}
          {field.value &&
            field.value.map((file, index) =>
              renderFileCard(file, index, 'formik'),
            )}
        </>
      )}

      {progress > 0 && (
        <div className="Loading">
          <div className="loading_page minthr-text-align-center">
            <p className="minthr-color-dark">{progress}%</p>
            <button
              type="button"
              className="minthr-btn minthr-btn-secondary mt-2"
              onClick={onCancelUpload}
            >
              {t('cancel')}
            </button>
          </div>
        </div>
      )}
    </>
  );
};

export default FileUpload;
